import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { Products } from './components/Products';
import { Categories } from './components/Categories';
import { Orders } from './components/Orders';
import { Settings } from './components/Settings';
import { PaymentGatewaySettings } from './components/PaymentGatewaySettings';
import { ProductFormPage } from './pages/ProductFormPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { authService } from './auth';
import { Toaster } from 'sonner';

export type NavItem = 'dashboard' | 'products' | 'categories' | 'orders' | 'settings';

function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="text-muted-foreground">{message}</div>
    </div>
  );
}

function AdminApp() {
  return (
    <div className="flex h-screen bg-background">
      <Toaster position="top-right" richColors />
      <Sidebar />

      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/new" element={<ProductFormPage />} />
            <Route path="/products/edit/:id" element={<ProductFormPage />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/payment-gateway" element={<PaymentGatewaySettings />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const path = location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      // Handle OAuth callback at /oauth-callback
      if (path === '/oauth-callback' && code && state) {
        setLoadingMessage('Completing authentication...');
        const success = await authService.handleCallback(code, state);
        // Redirect to home after callback
        navigate('/', { replace: true });
        setIsAuthenticated(success);
        setIsLoading(false);

        if (!success) {
          // If callback failed, redirect to login
          authService.initiateLogin();
        }
        return;
      }

      // Check existing auth
      if (authService.isAuthenticated()) {
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // Not authenticated - redirect to OAuth login
      setLoadingMessage('Redirecting to login...');
      setIsLoading(false);
      setIsAuthenticated(false);
      authService.initiateLogin();
    };

    checkAuth();
  }, [location.pathname, navigate]);

  if (isLoading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  if (!isAuthenticated) {
    return <LoadingScreen message="Redirecting to login..." />;
  }

  return <AdminApp />;
}
